import { AppMenuItem } from '@/core/permissions/permission.model';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

/** Classificação Administradora/Gestora de Serviços Médicos (única em que a aba Geridas existe). */
export const CLASSIFICACAO_ADMINISTRADORA = 32;

/** Segmentos de rota dos relatórios do módulo de prestadores (fonte única — use em routes e routerLink). */
export const PRESTADOR_RELATORIOS_ROUTES = {
    ROOT: 'relatorios',
    CERTIDAO_ALTERACAO_CONTRATUAL: 'certidao-alteracao-contratual-pj',
    CERTIDAO_RESPONSAVEL_TECNICO: 'certidao-responsavel-tecnico-pj',
    CERTIFICADO_REGULARIDADE: 'certificado-regularidade-pj',
    DECLARACAO_RESPONSABILIDADE: 'declaracao-responsabilidade-tecnica-pj',
    DECLARACAO_RT_PGRSS: 'declaracao-rt-pgrss-pj',
    OFICIO_DEFERIMENTO: 'oficio-deferimento-pj',
    TERMO_COMPROMISSO_DIRECAO: 'termo-compromisso-direcao-pj',
    TERMO_COMPROMISSO_PUBLICIDADE: 'termo-compromisso-publicidade-pj',
    TERMO_DEFINICAO_PRAZO: 'termo-definicao-prazo-pj',
    CERTIDAO_CANCELAMENTO: 'certidao-cancelamento-pj',
    CERTIDAO_DIRETOR_CLINICO: 'certidao-diretor-clinico-pj',
    CERTIFICADO_INSCRICAO: 'certificado-inscricao-pj',
    DECLARACAO_SUBSTITUICAO_DIRETOR: 'declaracao-substituicao-diretor-tecnico-pj',
    CART: 'cart-pj',
    CERTIDAO_SUSPENSAO: 'certidao-suspensao-pj',
    OFICIO_SUBSTITUICAO_DIRETOR: 'oficio-substituicao-diretor-tecnico-pj',
    REQUERIMENTO_RENOVACAO: 'requerimento-renovacao-pj',
    REQUERIMENTO_RENOVACAO_REGULARIDADE: 'requerimento-renovacao-regularidade-pj',
    DECLARACAO_DADOS_EMPRESA: 'declaracao-dados-empresa-pj',
    FICHA_CADASTRAL: 'ficha-cadastral-pj',
    RECIBO_PROTOCOLO: 'recibo-protocolo-pj',
    SOLICITACAO_DOCUMENTOS: 'solicitacao-documentos-pj',
    RELATORIO_SELECAO: 'relatorio-selecao-pj'
} as const;

/**
 * Slugs das 15 abas do módulo de Prestadores — 1:1 com `PERMISSOES.prestadores.<func>.escopo`.
 * Cada slug vira uma rota própria (`prestadores/<slug>`) gateada por `permissionGuard`.
 */
export const PRESTADORES_ABAS = [
    'dados-gerais',
    'documentos',
    'contatos',
    'diretoria',
    'corpo-societario',
    'composicao',
    'servicos-especialidades',
    'psmt',
    'comissao-etica',
    'recursos-humanos',
    'recursos-materiais',
    'atividades',
    'geridas',
    'historico',
    'anotacoes'
] as const;

export interface PrestadoresMenuActions {
    pesquisarPessoa: () => void;
}

/**
 * Monta o menu do módulo. O item "Geridas" só é incluído para prestadores com classificação
 * Administradora/Gestora de Serviços Médicos (`incluirGeridas`). As folhas usam `.escopo`
 * (qualquer role da funcionalidade libera) para concordar com o gate das rotas — ver
 * prestadores-menu-rota.spec.
 */
export function createPrestadoresMenu(actions: PrestadoresMenuActions, incluirGeridas: boolean): AppMenuItem[] {
    const rootPath = 'prestadores';
    const P = PERMISSOES.prestadores;

    const itensPrestador = [
        { label: 'Dados Gerais', icon: 'pi pi-fw pi-home', routerLink: [rootPath, 'dados-gerais'], permission: P.dadosGerais.escopo },
        { label: 'Documentos', icon: 'pi pi-fw pi-file', routerLink: [rootPath, 'documentos'], permission: P.documentos.escopo },
        { label: 'Contatos', icon: 'pi pi-fw pi-file', routerLink: [rootPath, 'contatos'], permission: P.contatos.escopo },
        { label: 'Diretoria', icon: 'pi pi-fw pi-users', routerLink: [rootPath, 'diretoria'], permission: P.diretoria.escopo },
        { label: 'Corpo Societário', icon: 'pi pi-fw pi-sitemap', routerLink: [rootPath, 'corpo-societario'], permission: P.corpoSocietario.escopo },
        { label: 'Composição', icon: 'pi pi-fw pi-list', routerLink: [rootPath, 'composicao'], permission: P.composicao.escopo },
        { label: 'Serviços/Especialidades', icon: 'pi pi-fw pi-cog', routerLink: [rootPath, 'servicos-especialidades'], permission: P.servicosEspecialidades.escopo },
        { label: 'PSMT', icon: 'pi pi-fw pi-shield', routerLink: [rootPath, 'psmt'], permission: P.psmt.escopo },
        { label: 'Comissão Ética', icon: 'pi pi-fw pi-shield', routerLink: [rootPath, 'comissao-etica'], permission: P.comissaoEtica.escopo },
        { label: 'Recursos Humanos', icon: 'pi pi-fw pi-users', routerLink: [rootPath, 'recursos-humanos'], permission: P.recursosHumanos.escopo },
        { label: 'Recursos Materiais', icon: 'pi pi-fw pi-box', routerLink: [rootPath, 'recursos-materiais'], permission: P.recursosMateriais.escopo },
        { label: 'Atividades', icon: 'pi pi-fw pi-briefcase', routerLink: [rootPath, 'atividades'], permission: P.atividades.escopo },
        ...(incluirGeridas ? [{ label: 'Geridas', icon: 'pi pi-fw pi-sitemap', routerLink: [rootPath, 'geridas'], permission: P.geridas.escopo }] : []),
        { label: 'Histórico', icon: 'pi pi-fw pi-history', routerLink: [rootPath, 'historico'], permission: P.historico.escopo },
        { label: 'Anotações', icon: 'pi pi-fw pi-book', routerLink: [rootPath, 'anotacoes'], permission: P.anotacoes.escopo }
    ];

    return [
        {
            label: 'Home',
            items: [
                {
                    label: 'Prestadores',
                    expanded: true,
                    items: itensPrestador
                },
                {
                    label: 'Pesquisar Pessoa',
                    icon: 'pi pi-fw pi-search',
                    command: actions.pesquisarPessoa
                },
                { label: 'Relatorios', icon: 'pi pi-fw pi-file-import', routerLink: [rootPath + '/relatorios'], permission: P.relatorios.escopo }
            ]
        }
    ];
}
